import type { ApolloCache, FetchResult } from '@apollo/client';
import { cloneDeep } from '@apollo/client/utilities';

import type {
  AiOriginalImageQuery,
  AiOriginalImageQueryVariables,
  AiOriginalImagesQuery,
  AiOriginalImagesQueryVariables,
  CreateAiOriginalImageMutation,
  UploadAiOriginalImageMutation,
} from 'src/shared/graphql/generated';
import {
  AiOriginalImageDocument,
  AiOriginalImagesDocument,
} from 'src/shared/graphql/generated';

import { logError } from '../../telemetry/logger';

type CreateAiOriginalImageResult = FetchResult<
  CreateAiOriginalImageMutation,
  Record<string, any>,
  Record<string, any>
>;

type UploadAiOriginalImageResult = FetchResult<
  UploadAiOriginalImageMutation,
  Record<string, any>,
  Record<string, any>
>;

export const createAiOriginalImagesCacheUpdater =
  (variables?: AiOriginalImagesQueryVariables) =>
  (cache: ApolloCache<any>, mutationResult: CreateAiOriginalImageResult) => {
    cache.updateQuery<AiOriginalImagesQuery, AiOriginalImagesQueryVariables>(
      {
        query: AiOriginalImagesDocument,
        variables,
      },
      prevData => {
        if (!mutationResult.data?.createAiOriginalImage) return undefined;

        const newOriginalImage = mutationResult.data?.createAiOriginalImage;

        if (!prevData?.aiOriginalImages) {
          return {
            aiOriginalImages: [
              {
                ...newOriginalImage,
                aiGeneratedImages: [],
              },
            ],
          };
        }

        const nextData = cloneDeep(prevData);
        nextData.aiOriginalImages?.push({
          ...newOriginalImage,
          aiGeneratedImages: [],
        });

        return nextData;
      },
    );
  };

export const createAiOriginalImageCacheUpdater =
  (variables?: AiOriginalImageQueryVariables) =>
  (cache: ApolloCache<any>, mutationResult: CreateAiOriginalImageResult) => {
    cache.updateQuery<AiOriginalImageQuery, AiOriginalImageQueryVariables>(
      {
        query: AiOriginalImageDocument,
        variables,
      },
      prevData => {
        if (!mutationResult.data?.createAiOriginalImage) return undefined;

        const newOriginalImage = mutationResult.data?.createAiOriginalImage;

        if (prevData?.aiOriginalImage) {
          logError(
            new Error(
              'Attempted to overwrite existing AI original image cache',
            ),
            { tags: { id: newOriginalImage.id } },
          );

          return undefined;
        }

        return {
          aiOriginalImage: { ...newOriginalImage, aiGeneratedImages: [] },
        };
      },
    );
  };

export const uploadAiOriginalImageCacheUpdater =
  (variables?: AiOriginalImageQueryVariables) =>
  (cache: ApolloCache<any>, mutationResult: UploadAiOriginalImageResult) => {
    cache.updateQuery<AiOriginalImageQuery, AiOriginalImageQueryVariables>(
      {
        query: AiOriginalImageDocument,
        variables,
      },
      prevData => {
        if (!mutationResult.data?.uploadAiOriginalImage) return undefined;

        const newOriginalImage = mutationResult.data?.uploadAiOriginalImage;

        if (prevData?.aiOriginalImage) {
          logError(
            new Error(
              'Attempted to overwrite existing AI original image cache',
            ),
            { tags: { id: newOriginalImage.id } },
          );

          return undefined;
        }

        return {
          aiOriginalImage: { ...newOriginalImage, aiGeneratedImages: [] },
        };
      },
    );
  };

export const uploadAiOriginalImagesCacheUpdater =
  (variables?: AiOriginalImagesQueryVariables) =>
  (cache: ApolloCache<any>, mutationResult: UploadAiOriginalImageResult) => {
    cache.updateQuery<AiOriginalImagesQuery, AiOriginalImagesQueryVariables>(
      {
        query: AiOriginalImagesDocument,
        variables,
      },
      prevData => {
        if (!mutationResult.data?.uploadAiOriginalImage) return undefined;

        const newOriginalImage = mutationResult.data?.uploadAiOriginalImage;

        if (!prevData?.aiOriginalImages) {
          return {
            aiOriginalImages: [
              {
                ...newOriginalImage,
                aiGeneratedImages: [],
              },
            ],
          };
        }

        const nextData = cloneDeep(prevData);
        nextData.aiOriginalImages?.push({
          ...newOriginalImage,
          aiGeneratedImages: [],
        });

        return nextData;
      },
    );
  };
